import { axiosBaseQuery } from '@client';
import { createApi } from '@reduxjs/toolkit/query/react';
import { DanaosTaskResponse } from '@state/stores/danaos/DanaosTaskResponse';

export interface DanaosBuyerApiPropertiesDto {
  id: number;
  buyerId: number;
  properties: string;
}

export const danaosApi = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: 'secured/danaos' }),
  tagTypes: ['DANAOS_PROTOCOL', 'DANAOS_TASK', 'DANAOS_BUYER_PROPERTIES'],
  reducerPath: 'danaos-api',
  endpoints: (build) => ({
    getBuyerApiProperties: build.query<DanaosBuyerApiPropertiesDto[], void>({
      query: () => ({
        url: '/admin/buyer-api-properties',
        method: 'GET'
      }),
      providesTags: ['DANAOS_BUYER_PROPERTIES']
    }),

    getBuyerApiPropertiesById: build.query<DanaosBuyerApiPropertiesDto, number>(
      {
        query: (id) => ({
          url: `/admin/buyer-api-properties/${id}`,
          method: 'GET'
        }),
        providesTags: (_result, _error, id) => [
          { type: 'DANAOS_BUYER_PROPERTIES', id }
        ]
      }
    ),

    createBuyerApiProperties: build.mutation<
      DanaosBuyerApiPropertiesDto,
      { buyerId: number; properties: string }
    >({
      query: (body) => ({
        url: '/admin/buyer-api-properties',
        method: 'POST',
        data: body
      }),
      invalidatesTags: ['DANAOS_BUYER_PROPERTIES']
    }),

    updateBuyerApiProperties: build.mutation<
      DanaosBuyerApiPropertiesDto,
      { id: number; buyerId: number; properties: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/buyer-api-properties/${id}`,
        method: 'PUT',
        data: body
      }),
      invalidatesTags: ['DANAOS_BUYER_PROPERTIES']
    }),

    sendProtocolEvent: build.mutation<
      { success: boolean; message?: string },
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: '/protocol',
        method: 'POST',
        data: { evaluationId }
      }),
      invalidatesTags: (res, _err, { evaluationId }) =>
        res
          ? [{ type: 'DANAOS_PROTOCOL', id: evaluationId }]
          : ['DANAOS_PROTOCOL']
    }),

    // Task status endpoints
    getTask: build.query<
      DanaosTaskResponse,
      { entityType: string; id: number; buyerId: number }
    >({
      query: ({ entityType, id, buyerId }) => ({
        url: `/task/${entityType}/${id}?buyerId=${buyerId}`,
        method: 'GET'
      }),
      providesTags: (result, _error, { entityType, id }) =>
        result
          ? [{ type: 'DANAOS_TASK', id: `${entityType}-${id}` }]
          : ['DANAOS_TASK']
    }),

    getTaskDetails: build.query<
      DanaosTaskResponse,
      { entityType: string; id: number; buyerId: number }
    >({
      query: ({ entityType, id, buyerId }) => ({
        url: `/task-details/${entityType}/${id}?buyerId=${buyerId}`,
        method: 'GET'
      }),
      providesTags: (result, _error, { entityType, id }) =>
        result
          ? [{ type: 'DANAOS_TASK', id: `${entityType}-${id}` }]
          : ['DANAOS_TASK']
    }),

    getAvailableFlows: build.query<string[], void>({
      query: () => ({
        url: '/flows',
        method: 'GET'
      })
    }),
    createProformaProtocolTask: build.mutation<string, { proformaId: number }>({
      query: ({ proformaId }) => ({
        url: '/proforma-protocol-export',
        method: 'POST',
        params: { proformaId }
      })
    }),
    createAppointmentAcceptedTask: build.mutation<
      string,
      { appointmentId: number }
    >({
      query: ({ appointmentId }) => ({
        url: '/trigger-accept-appointment',
        method: 'POST',
        params: { appointmentId }
      })
    })
  })
});

export type StateWithDanaosApi = {
  [danaosApi.reducerPath]: ReturnType<typeof danaosApi.reducer>;
};

export const {
  useSendProtocolEventMutation,
  useGetTaskQuery,
  useGetTaskDetailsQuery,
  useGetAvailableFlowsQuery,
  useCreateProformaProtocolTaskMutation,
  useCreateAppointmentAcceptedTaskMutation,
  useGetBuyerApiPropertiesQuery,
  useGetBuyerApiPropertiesByIdQuery,
  useCreateBuyerApiPropertiesMutation,
  useUpdateBuyerApiPropertiesMutation
} = danaosApi;
